import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiArrowLeft, FiPhone } from 'react-icons/fi';
import supabase from '../services/supabase';
import CountryCodePicker from '../components/CountryCodePicker';
import '../styles/Auth.scss';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || null;
  const prefillPhone = location.state?.phone || '';
  const prefillCountryCode = location.state?.countryCode || '+91';

  const [loginMethod, setLoginMethod] = useState(prefillPhone ? 'phone' : 'email');
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(prefillPhone);
  const [phonePassword, setPhonePassword] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(
    prefillCountryCode !== '+91'
      ? { code: prefillCountryCode, flag: '', name: '', iso2: '' }
      : { code: '+91', flag: '🇮🇳', name: 'India', iso2: 'IN' }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (signInError) throw signInError;

      if (data?.user) {
        if (redirectTo) {
          navigate(redirectTo);
          return;
        }
        // Check if profile is complete
        const { data: profile } = await supabase
          .from('users')
          .select('first_name, last_name, username')
          .eq('id', data.user.id)
          .single();

        if (profile && profile.first_name && profile.last_name) {
          if (profile.username) {
            navigate(`/${profile.username}`);
          } else {
            navigate('/explore');
          }
        } else {
          navigate('/profile-setup');
        }
      }
    } catch (error) {
      console.error('Login error:', error);

      if (error.message?.includes('Invalid login credentials')) {
        setError('Invalid email or password');
      } else {
        setError(error.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.trim() || !phonePassword) {
      setError('Please enter your phone number and password');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const formattedPhoneNumber = `${selectedCountry.code}${phoneNumber}`;

      // Get email associated with phone number
      const { data: userData, error: lookupError } = await supabase
        .from('users')
        .select('email')
        .eq('phone_number', formattedPhoneNumber)
        .single();

      if (lookupError || !userData?.email) {
        throw new Error('No account found with this phone number');
      }

      // Sign in with email
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: userData.email,
        password: phonePassword,
      });

      if (signInError) throw signInError;

      if (data?.user) {
        if (redirectTo) {
          navigate(redirectTo);
          return;
        }
        const { data: profile } = await supabase
          .from('users')
          .select('first_name, last_name, username')
          .eq('id', data.user.id)
          .single();

        if (profile && profile.first_name && profile.last_name) {
          if (profile.username) {
            navigate(`/${profile.username}`);
          } else {
            navigate('/explore');
          }
        } else {
          navigate('/profile-setup');
        }
      }
    } catch (error) {
      console.error('Login error:', error);

      if (error.message?.includes('Invalid login credentials') ||
          error.message?.includes('No account found')) {
        setError('Invalid phone number or password');
      } else {
        setError(error.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <img src="/images/events/6.jpg" alt="Background" />
        <div className="auth-overlay"></div>
      </div>

      <div className="auth-content">
        <Link to="/" className="back-button">
          <FiArrowLeft />
        </Link>

        <motion.div
          className="auth-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="auth-title">Welcome back!</h1>
          {redirectTo && (
            <p className="auth-redirect-note">Sign in to complete your RSVP — you'll be taken right back.</p>
          )}

          <div className="auth-tabs">
            <button
              className={`tab ${loginMethod === 'email' ? 'active' : ''}`}
              onClick={() => {
                setLoginMethod('email');
                setError(null);
              }}
            >
              Email
            </button>
            <button
              className={`tab ${loginMethod === 'phone' ? 'active' : ''}`}
              onClick={() => {
                setLoginMethod('phone');
                setError(null);
              }}
            >
              Phone
            </button>
          </div>

          {loginMethod === 'email' ? (
            <form onSubmit={handleEmailLogin}>
              <div className="input-group">
                <label>Email</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
              </div>

              <div className="input-group">
                <label>Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
              </div>

              {error && <p className="error-text">{error}</p>}

              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || !email.trim() || !password}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePhoneLogin}>
              <div className="input-group">
                <label>Phone Number</label>
                <div className="phone-input-wrapper">
                  <CountryCodePicker
                    value={selectedCountry.code}
                    onChange={setSelectedCountry}
                  />
                  <div className="input-with-icon flex-1">
                    <FiPhone className="input-icon" />
                    <input
                      type="tel"
                      placeholder="Enter phone number"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''));
                        setError(null);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="input-group">
                <label>Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={phonePassword}
                    onChange={(e) => {
                      setPhonePassword(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
              </div>

              {error && <p className="error-text">{error}</p>}

              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading || !phoneNumber.trim() || !phonePassword}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          <div className="auth-footer">
            <p>Don't have an account? <Link to="/signup">Sign Up</Link></p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;